import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useViewConfig } from '../hooks/useViewConfig';
import { getProductById } from '../services/productService';
import { slugifySection } from '../utils/sectionUtils';
import ProductNavbar from '../components/product/ProductNavbar';
import ProductGallery from '../components/product/ProductGallery';
import ProductInfo from '../components/product/ProductInfo';
import TrustIndicators from '../components/product/TrustIndicators';
import RelatedProducts from '../components/product/RelatedProducts';
import ProductSkeleton from '../components/product/ProductSkeleton';
import '../styles/productDetail.css';

/**
 * Hex to RGB string converter helper.
 * Outputs format "R, G, B" to enable rgba(var(--section-color-rgb), alpha) syntax.
 */
const hexToRgb = (hex) => {
  if (!hex) return '255, 106, 0';
  let shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '255, 106, 0';
};

/**
 * Controller/Container component representing the Product Detail Page.
 * Coordinates product details fetching, section config colors, layouts, trust, and recommendations.
 */
const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch sections config to determine the theme color accent
  const { data: configData } = useViewConfig("HOME_SECTIONS");

  // Toggle active class on document body on mount/unmount to hide top global navbar on mobile
  useEffect(() => {
    document.body.classList.add('product-detail-page-active');
    return () => {
      document.body.classList.remove('product-detail-page-active');
    };
  }, []);

  // Fetch single product data on mount or when id changes
  useEffect(() => {
    let isMounted = true;

    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getProductById(id);
        if (isMounted) {
          setProduct(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch product details.');
          console.error(`Error loading product ${id}:`, err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (id) {
      fetchProductDetails();
    }

    return () => {
      isMounted = false;
    };
  }, [id]);

  // Dynamically update page document title based on product details (Wholesale format)
  useEffect(() => {
    if (product) {
      document.title = `${product.name} | REGARD TRADERS`;
    }
    return () => {
      document.title = 'REGARD TRADERS';
    };
  }, [product]);

  // Resolve section-driven accent color dynamically from ViewConfig API without switch maps
  const sectionColor = useMemo(() => {
    if (!product || !product.section) return '#ff6a00';

    const sectionsList = configData?.data?.sections || [];
    const matched = sectionsList.find(
      (sec) => sec.title.toLowerCase() === product.section.toLowerCase()
    );

    return matched?.color || '#ff6a00';
  }, [product, configData]);

  // Derive RGB string for alpha-transparent styling
  const sectionRGB = useMemo(() => hexToRgb(sectionColor), [sectionColor]);

  // Inject CSS variables for theme accents
  const pageThemeStyle = {
    '--section-color': sectionColor,
    '--section-color-rgb': sectionRGB,
  };

  // 1. Loading State
  if (loading) {
    return <ProductSkeleton />;
  }

  // 2. Error State
  if (error || !product) {
    return (
      <div className="product-error-container">
        <div className="product-error-icon">
          <AlertCircle size={40} />
        </div>
        <h2 className="product-error-title">Product Not Found</h2>
        <p className="product-error-message">
          We couldn't retrieve the details for this product. It may have been removed,
          marked unavailable, or the link is invalid.
        </p>
        <Link to="/shop" className="btn">
          Back To Products
        </Link>
      </div>
    );
  }

  // 3. Render Redesigned Page Layout
  return (
    <div style={pageThemeStyle} className="product-page-body">
      {/* Mobile-Only custom top header bar */}
      <ProductNavbar />

      <div className="product-detail-container">
        {/* Desktop Breadcrumbs (Hidden on mobile via CSS) */}
        <div className="product-breadcrumb">
          <Link to="/">Home</Link>
          <span className="product-breadcrumb-separator">&gt;</span>
          <Link to={`/section/${slugifySection(product.section)}`}>
            {product.section}
          </Link>
          <span className="product-breadcrumb-separator">&gt;</span>
          <span>{product.category?.name || 'General'}</span>
          <span className="product-breadcrumb-separator">&gt;</span>
          <span className="product-breadcrumb-active">{product.name}</span>
        </div>

        {/* 2-Column responsive layout structure (Wide Gallery + Glassmorphic Card) */}
        <div className="product-layout-grid">
          {/* Gallery Horizontal Layout (Large Image on Top, Horizontal Thumbnails below) */}
          <ProductGallery images={product.images} sectionColor={sectionColor} />

          {/* Details Information Panel (Name, Price, MOQ, CTAs, Description, Specs) */}
          <ProductInfo product={product} sectionColor={sectionColor} />
        </div>

        {/* Trust Indicators Section */}
        <TrustIndicators />

        {/* Dynamic Related Products Section */}
        <RelatedProducts
          currentProductId={product._id}
          sectionColor={sectionColor}
        />
      </div>
    </div>
  );
};

export default ProductDetail;
